import i18n from "../i18n";
import React, { useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Image,
  NumberInput,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  Loader,
  Accordion,
} from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { Dropzone, IMAGE_MIME_TYPE, PDF_MIME_TYPE } from "@mantine/dropzone";
import { showNotification } from "@mantine/notifications";
import { IconUpload, IconCheck, IconTrash, IconPencil } from "@tabler/icons";
import moment from "moment";
import _ from "lodash";

import { useServerApi } from "../hooks/userServerApi";
import DataTable from "./dataTable";
import ActionCellRender from "./actionCellRender";
import { ImageThumbList } from "./clientPaymentList";
import { getFileUrl, isImageFile, isPDFFile } from "./fileList";
import { renderCpdScoresCell } from "./cpdScoresCell";
import {
  CPD_CREDIT_SECTIONS,
  applyCpdParseDraft,
  createEmptyCpdDraft,
  getCpdCreditTotal,
  parseCpdDateEnroll,
} from "../data/cpdFormFields";

const STEPS = ["Upload certificates", "Course details", "Confirm & submit"];

const isPdfFile = (file) => {
  const name = file?.name?.toLowerCase?.() ?? "";
  const mime = file?.mimetype || file?.type || "";
  return (
    isPDFFile(mime) ||
    mime === "application/pdf" ||
    name.endsWith(".pdf")
  );
};

const isImageCertificate = (file) => {
  if (isPdfFile(file)) return false;
  return isImageFile(file?.mimetype) || file?.type?.startsWith?.("image/");
};

const previewPanelSx = {
  border: "1px solid var(--mantine-color-dark-4)",
  borderRadius: "var(--mantine-radius-md)",
  overflow: "hidden",
  background: "var(--mantine-color-dark-7)",
  minHeight: "calc(100vh - 280px)",
  height: "calc(100vh - 280px)",
};

const getPdfViewerUrl = (url) => {
  const base = url.split("#")[0];
  // navpanes=0 hides the thumbnail sidebar in Chrome's built-in PDF viewer
  return `${base}#navpanes=0&toolbar=1&scrollbar=1&view=FitH`;
};

const PdfDocumentPreview = ({ url, title }) => {
  const viewerUrl = getPdfViewerUrl(url);

  return (
    <Box sx={previewPanelSx}>
      <iframe
        src={viewerUrl}
        title={title}
        width="100%"
        height="100%"
        style={{
          border: 0,
          display: "block",
          minHeight: "calc(100vh - 280px)",
        }}
      />
    </Box>
  );
};

const CertificatePreview = ({ file }) => {
  const url = getFileUrl(file);

  if (!url) {
    return (
      <Box
        sx={{
          ...previewPanelSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text size="sm" color="dimmed">
          No preview available
        </Text>
      </Box>
    );
  }

  if (isPdfFile(file)) {
    return (
      <PdfDocumentPreview url={url} title={file?.name || "Certificate PDF"} />
    );
  }

  if (isImageCertificate(file)) {
    return (
      <Box
        sx={{
          ...previewPanelSx,
          overflow: "auto",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        <Image
          src={url}
          alt={file?.name || "Certificate"}
          fit="contain"
          styles={{
            root: { width: "100%", maxWidth: "100%" },
            image: { width: "100%", height: "auto", maxWidth: "100%" },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...previewPanelSx,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack align="center" spacing="sm">
        <Text size="sm" color="dimmed">
          {file?.name}
        </Text>
        <Button
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          variant="light"
          size="xs"
        >
          Open file
        </Button>
      </Stack>
    </Box>
  );
};

const CpdDraftEntry = ({ draft, onChange }) => (
  <Paper withBorder p="md" radius="md">
    <Group position="apart" mb="sm">
      <Text size="sm" weight={500}>
        {draft.file?.name || "Certificate"}
      </Text>
      {draft.parsing ? (
        <Group spacing="xs">
          <Loader size="xs" />
          <Text size="xs">Reading certificate...</Text>
        </Group>
      ) : (
        <Badge
          color={
            draft.confidence === "high"
              ? "green"
              : draft.confidence === "partial"
                ? "yellow"
                : "orange"
          }
        >
          OCR {draft.confidence}
        </Badge>
      )}
    </Group>
    {!draft.parsing && draft.confidence !== "high" && (
      <Alert color="yellow" mb="sm">
        Some fields could not be read from the certificate. Please verify all
        details before submitting.
      </Alert>
    )}
    <SimpleGrid
      cols={2}
      spacing="lg"
      breakpoints={[{ maxWidth: "md", cols: 1 }]}
      sx={{ alignItems: "flex-start" }}
    >
      <CertificatePreview file={draft.file} />
      <CpdDraftForm draft={draft} onChange={onChange} />
    </SimpleGrid>
  </Paper>
);

const createDraftEntry = (fileMeta) => ({
  id: _.uniqueId("cpd-draft-"),
  file: fileMeta,
  parsing: true,
  confidence: "low",
  rawText: "",
  ...createEmptyCpdDraft(),
  files: [fileMeta],
});

const CpdDraftForm = ({ draft, onChange }) => {
  const update = (field, value) => onChange({ ...draft, [field]: value });

  return (
    <Stack spacing="sm">
      <DatePicker
        label="Course date"
        required
        value={parseCpdDateEnroll(draft.dateEnroll)}
        onChange={(value) => update("dateEnroll", parseCpdDateEnroll(value))}
        allowFreeInput
      />
      <TextInput
        label="Course title"
        required
        value={draft.courseTitle}
        onChange={(e) => update("courseTitle", e.currentTarget.value)}
      />
      <TextInput
        label="Course code"
        value={draft.courseCode}
        onChange={(e) => update("courseCode", e.currentTarget.value)}
      />
      <Stack spacing="md">
        {CPD_CREDIT_SECTIONS.map((section) => (
          <SimpleGrid
            key={section.key}
            cols={section.fields.length}
            spacing="sm"
            breakpoints={[{ maxWidth: "sm", cols: 1 }]}
          >
            {section.fields.map((field) => (
              <NumberInput
                key={field.name}
                label={field.label}
                precision={1}
                min={0}
                value={draft[field.name] ?? 0}
                onChange={(value) => update(field.name, value ?? 0)}
              />
            ))}
          </SimpleGrid>
        ))}
      </Stack>
      {draft.rawText && (
        <Accordion variant="contained">
          <Accordion.Item value="raw">
            <Accordion.Control>{i18n.t("View extracted text")}</Accordion.Control>
            <Accordion.Panel>
              <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>
                {draft.rawText}
              </Text>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}
    </Stack>
  );
};

const ConsultantCpdSubmitWizard = ({ onSubmitted, onCancel }) => {
  const [api] = useServerApi();
  const [step, setStep] = useState(0);
  const [drafts, setDrafts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingDraft, setEditingDraft] = useState(null);

  const handleUpload = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const uploadResult = await api.uploadFile("files", file, null, []);
        if (!uploadResult?.data) continue;

        const fileMeta = uploadResult.data;
        const entry = createDraftEntry(fileMeta);
        setDrafts((prev) => [...prev, entry]);

        try {
          const parseResult = await api.Cpd.parseCertificate({
            fileLink: fileMeta.link,
          });
          const payload = parseResult?.data ?? {};
          setDrafts((prev) =>
            prev.map((item) =>
              item.id === entry.id
                  ? {
                    ...item,
                    parsing: false,
                    confidence: payload.confidence ?? "low",
                    rawText: payload.rawText ?? "",
                    ...applyCpdParseDraft(payload.draft ?? {}),
                    files: [fileMeta],
                  }
                : item
            )
          );
        } catch (error) {
          setDrafts((prev) =>
            prev.map((item) =>
              item.id === entry.id ? { ...item, parsing: false } : item
            )
          );
        }
      }
      if (files.length) setStep(1);
    } finally {
      setUploading(false);
    }
  };

  const updateDraft = (id, nextDraft) => {
    setDrafts((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...nextDraft } : item))
    );
  };

  const removeDraft = (id) => {
    setDrafts((prev) => prev.filter((item) => item.id !== id));
  };

  const validateDrafts = () => {
    for (let i = 0; i < drafts.length; i += 1) {
      const draft = drafts[i];
      if (!draft.dateEnroll) {
        return `Entry ${i + 1}: course date is required`;
      }
      if (!draft.courseTitle?.trim?.()) {
        return `Entry ${i + 1}: course title is required`;
      }
      if (getCpdCreditTotal(draft) <= 0) {
        return `Entry ${i + 1}: at least one credit hour is required`;
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateDrafts();
    if (validationError) {
      showNotification({ color: "red", message: validationError });
      return;
    }

    setSubmitting(true);
    try {
      const records = drafts.map((draft) => ({
        dateEnroll: draft.dateEnroll,
        courseTitle: draft.courseTitle,
        courseCode: draft.courseCode,
        IACredit: draft.IACredit ?? 0,
        IAERCredit: draft.IAERCredit ?? 0,
        IAROCredit: draft.IAROCredit ?? 0,
        IAILASCredit: draft.IAILASCredit ?? 0,
        MPFCoreCredit: draft.MPFCoreCredit ?? 0,
        MPFNonCoreCredit: draft.MPFNonCoreCredit ?? 0,
        SFCCredit: draft.SFCCredit ?? 0,
        SFCECCredit: draft.SFCECCredit ?? 0,
        files: draft.files,
      }));

      await api.Cpd.submitConsultantBatch({ records });

      showNotification({
        color: "green",
        message: "CPD records submitted for approval",
      });
      onSubmitted?.();
    } catch (error) {
      showNotification({
        color: "red",
        message:
          typeof error === "string"
            ? error
            : error?.message || "Failed to submit CPD records",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const reviewColumns = [
    {
      field: "dateEnroll",
      headerName: "Course Date",
      cellRender: (col, data) =>
        data.dateEnroll ? moment(data.dateEnroll).format("LL") : "—",
    },
    {
      field: "courseTitle",
      headerName: "Course",
      cellRender: (col, data) => (
        <Stack spacing={2}>
          <Text size="xs">{data.courseTitle}</Text>
          <Text size="xs" color="dimmed">
            {data.courseCode || "—"}
          </Text>
        </Stack>
      ),
    },
    {
      field: "scores",
      headerName: "Credits",
      cellRender: renderCpdScoresCell,
    },
    {
      field: "files",
      headerName: "Certificate",
      cellRender: (col, data) => ImageThumbList({ files: data.files }),
    },
    {
      field: "action",
      isAction: true,
      cellRender: ActionCellRender,
      actions: [
        {
          name: "edit",
          icon: IconPencil,
          label: "Edit",
        },
      ],
      actionMenu: [
        {
          name: "delete",
          label: "Remove",
          icon: IconTrash,
        },
      ],
    },
  ];

  return (
    <>
      <Group spacing="xs" mb="lg">
        {STEPS.map((label, index) => (
          <Badge
            key={label}
            color={step === index ? "cyan" : step > index ? "green" : "gray"}
            variant={step === index ? "filled" : "outline"}
          >
            {index + 1}. {label}
          </Badge>
        ))}
      </Group>

      {step === 0 && (
        <Stack>
          <Dropzone
            onDrop={handleUpload}
            accept={[...IMAGE_MIME_TYPE, ...PDF_MIME_TYPE]}
            multiple
            disabled={uploading}
          >
            <Group position="center" spacing="xl" style={{ minHeight: 120 }}>
              <Dropzone.Accept>
                <IconUpload size={40} />
              </Dropzone.Accept>
              <Dropzone.Idle>
                <Stack align="center" spacing="xs">
                  <IconUpload size={40} />
                  <Text size="sm">
                    Drop certificate images or PDFs here, or click to browse
                  </Text>
                </Stack>
              </Dropzone.Idle>
            </Group>
          </Dropzone>
          {onCancel && (
            <Group>
              <Button variant="default" onClick={onCancel}>{i18n.t("Cancel")}</Button>
            </Group>
          )}
          {drafts.length > 0 && (
            <Button onClick={() => setStep(1)}>{i18n.t("Continue to details")}</Button>
          )}
        </Stack>
      )}

      {step === 1 && (
        <Stack>
          {drafts.map((draft) => (
            <CpdDraftEntry
              key={draft.id}
              draft={draft}
              onChange={(nextDraft) => updateDraft(draft.id, nextDraft)}
            />
          ))}
          <Group position="apart">
            <Button variant="default" onClick={() => setStep(0)}>{i18n.t("Back")}</Button>
            <Button
              disabled={!drafts.length || drafts.some((d) => d.parsing)}
              onClick={() => {
                const error = validateDrafts();
                if (error) {
                  showNotification({ color: "red", message: error });
                  return;
                }
                setStep(2);
              }}
            >
              Review submission
            </Button>
          </Group>
        </Stack>
      )}

      {step === 2 && (
        <Stack>
          <DataTable
            data={drafts}
            columns={reviewColumns}
            hideSearchBar
            hidePagination
            onActionBtnClick={({ action, data }) => {
              if (action === "edit") setEditingDraft(data);
              if (action === "delete") removeDraft(data.id);
            }}
          />
          <Group position="apart">
            <Button variant="default" onClick={() => setStep(1)}>{i18n.t("Back")}</Button>
            <Button
              leftIcon={<IconCheck size={16} />}
              loading={submitting}
              onClick={handleSubmit}
            >
              Confirm & submit for approval
            </Button>
          </Group>
        </Stack>
      )}

      {editingDraft && (
        <Paper withBorder p="md" mt="md" radius="md">
          <Title order={6} mb="sm">
            Edit entry
          </Title>
          <SimpleGrid
            cols={2}
            spacing="lg"
            breakpoints={[{ maxWidth: "md", cols: 1 }]}
            sx={{ alignItems: "flex-start" }}
          >
            <CertificatePreview file={editingDraft.file} />
            <CpdDraftForm
              draft={editingDraft}
              onChange={(nextDraft) => {
                updateDraft(editingDraft.id, nextDraft);
                setEditingDraft({ ...editingDraft, ...nextDraft });
              }}
            />
          </SimpleGrid>
          <Button mt="md" onClick={() => setEditingDraft(null)}>{i18n.t("Done")}</Button>
        </Paper>
      )}
    </>
  );
};

export default ConsultantCpdSubmitWizard;
